const EmblaCarousel = globalThis.EmblaCarousel;
const EmblaCarouselAutoplay = globalThis.EmblaCarouselAutoplay;
const EmblaCarouselFade = globalThis.EmblaCarouselFade;
const EmblaCarouselSwipe = globalThis.EmblaCarouselSwipe;

if (!customElements.get('slideshow-embla')) {
  class SlideshowEmbla extends HTMLElement {
    constructor() {
      super();

      this.animations = [];
      this.activeIndex = 0;
      this.animationsEnabled = this.isGsapEnabled();
      this.transitionType = 'slide';
      this.config = {};
      this.useFadePlugin = false;
      this.useSwipePlugin = false;

      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onSelect = this.onSelect.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('.embla__viewport');
      this.slides = Array.from(this.querySelectorAll('.embla__slide'));
      this.dots = Array.from(this.querySelectorAll('.slideshow-embla__dot-button'));
      this.counterCurrent = this.parentElement?.querySelector('.slideshow-embla__counter-current') || null;
      this.counterTotal = this.parentElement?.querySelector('.slideshow-embla__counter-total') || null;
      this.prevButton = this.parentElement?.querySelector('.slideshow-embla__prev') || null;
      this.nextButton = this.parentElement?.querySelector('.slideshow-embla__next') || null;

      if (!this.viewport || this.slides.length < 1) return;

      this.config = this.readConfig();
      this.transitionType = this.config.transition;

      if (this.slides.length === 1) {
        this.initSingleSlide();
        return;
      }

      if (typeof EmblaCarousel !== 'function') return;

      const autoplayDelay = this.config.autoplay;
      const shouldLoop = this.config.loop;
      this.useFadePlugin = this.transitionType === 'fade' && typeof EmblaCarouselFade === 'function';
      this.useSwipePlugin = this.transitionType === 'swipe' && typeof EmblaCarouselSwipe === 'function';
      const plugins = [];

      if (autoplayDelay > 0) {
        if (typeof EmblaCarouselAutoplay === 'function') {
          plugins.push(
            EmblaCarouselAutoplay({
              delay: autoplayDelay,
              stopOnInteraction: false,
              stopOnMouseEnter: true,
              stopOnFocusIn: true
            })
          );
        }
      }

      if (this.useFadePlugin) {
        plugins.push(EmblaCarouselFade());
      }

      if (this.useSwipePlugin) {
        plugins.push(
          EmblaCarouselSwipe({
            duration: 700
          })
        );
      }

      this.embla = EmblaCarousel(
        this.viewport,
        {
          align: 'start',
          loop: shouldLoop,
          dragFree: false,
          containScroll: 'trimSnaps',
          watchDrag: !this.useSwipePlugin
        },
        plugins
      );

      this.embla.on('select', this.onSelect);
      this.embla.on('reInit', this.onSelect);

      if (this.animationsEnabled) {
        this.prepareAnimations();
      }

      this.onSelect();

      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.onPrevClick);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.onNextClick);
      }

      for (const [index, dot] of this.dots.entries()) {
        dot.addEventListener('click', () => {
          this.embla?.scrollTo(index);
        });
      }

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    disconnectedCallback() {
      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this.onPrevClick);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.onNextClick);
      }

      if (Shopify.designMode) {
        this.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      if (this.embla) {
        this.embla.destroy();
      }
    }

    initSingleSlide() {
      this.slides[0].classList.add('is-selected');
      this.updateVideos(0);

      if (this.animationsEnabled) {
        this.prepareAnimations();
        this.animateSlide(0);
      }
    }

    onPrevClick() {
      this.embla?.scrollPrev();
    }

    onNextClick() {
      this.embla?.scrollNext();
    }

    onBlockSelect(event) {
      const index = this.slides.indexOf(event.target);
      if (index === -1) return;

      if (!this.embla) return;
      this.embla.scrollTo(index);
    }

    onSelect() {
      if (!this.embla) return;

      const selectedIndex = this.embla.selectedScrollSnap();
      const previousIndex = this.activeIndex;

      this.updateSlides(selectedIndex);
      this.updateDots(selectedIndex);
      this.updateCounter(selectedIndex);
      this.updateControls();
      this.updateVideos(selectedIndex);

      if (this.animationsEnabled) {
        if (previousIndex !== selectedIndex) {
          window.setTimeout(() => {
            this.animateReverse(previousIndex);
          }, 300);
        }

        this.animateSlide(selectedIndex);
      }

      this.activeIndex = selectedIndex;
    }

    updateSlides(selectedIndex) {
      for (const [index, slide] of this.slides.entries()) {
        slide.classList.toggle('is-selected', index === selectedIndex);
      }
    }

    updateDots(selectedIndex) {
      for (const [index, dot] of this.dots.entries()) {
        const isSelected = index === selectedIndex;
        dot.parentElement?.classList.toggle('is-selected', isSelected);
        if (isSelected) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      }
    }

    updateCounter(selectedIndex) {
      if (!this.counterCurrent || !this.counterTotal) return;

      this.counterCurrent.textContent = `${selectedIndex + 1}`;
      this.counterTotal.textContent = `${this.slides.length}`;
    }

    updateControls() {
      if (!this.embla) return;

      if (this.prevButton) {
        this.prevButton.disabled = !this.embla.canScrollPrev();
      }

      if (this.nextButton) {
        this.nextButton.disabled = !this.embla.canScrollNext();
      }
    }

    updateVideos(selectedIndex) {
      for (const [index, slide] of this.slides.entries()) {
        const videoContainer = slide.querySelector('.slideshow__slide-video-bg');
        if (!videoContainer) continue;

        if (index === selectedIndex) {
          this.playVideo(videoContainer);
        } else {
          this.pauseVideo(videoContainer);
        }
      }
    }

    pauseVideo(videoContainer) {
      if (videoContainer.dataset.provider === 'hosted') {
        const video = videoContainer.querySelector('video');
        if (video) video.pause();
        return;
      }

      const iframe = videoContainer.querySelector('iframe');
      if (!iframe || !iframe.src) return;

      if (videoContainer.dataset.provider === 'youtube') {
        iframe.contentWindow?.postMessage(
          JSON.stringify({
            event: 'command',
            func: 'pauseVideo',
            args: ''
          }),
          '*'
        );
      }

      if (videoContainer.dataset.provider === 'vimeo') {
        iframe.contentWindow?.postMessage(
          JSON.stringify({
            method: 'pause'
          }),
          '*'
        );
      }
    }

    playVideo(videoContainer) {
      if (videoContainer.dataset.provider === 'hosted') {
        const video = videoContainer.querySelector('video');
        if (video) video.play();
        return;
      }

      const iframe = videoContainer.querySelector('iframe');
      if (!iframe) return;

      if (!iframe.src && iframe.dataset.src) {
        iframe.src = iframe.dataset.src;
      }

      if (!iframe.src) return;

      if (videoContainer.dataset.provider === 'youtube') {
        iframe.contentWindow?.postMessage(
          JSON.stringify({
            event: 'command',
            func: 'playVideo',
            args: ''
          }),
          '*'
        );
      }

      if (videoContainer.dataset.provider === 'vimeo') {
        iframe.contentWindow?.postMessage(
          JSON.stringify({
            method: 'play'
          }),
          '*'
        );
      }
    }

    isGsapEnabled() {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      return (
        document.body.classList.contains('animations-true') &&
        typeof globalThis.gsap !== 'undefined' &&
        typeof globalThis.SplitText !== 'undefined' &&
        !reduceMotion
      );
    }

    readConfig() {
      const configElement = this.querySelector('.slideshow-embla__config');
      const fallbackTransition = this.getTransitionFromClass();
      const config = {
        autoplay: 0,
        loop: false,
        dots: true,
        controls: true,
        transition: fallbackTransition
      };

      if (!configElement?.textContent) {
        return config;
      }

      try {
        const parsed = JSON.parse(configElement.textContent);
        const parsedAutoplay = Number.parseInt(String(parsed?.autoplay ?? 0), 10);

        if (Number.isFinite(parsedAutoplay) && parsedAutoplay > 0) {
          config.autoplay = parsedAutoplay;
        }

        if (typeof parsed?.loop === 'boolean') {
          config.loop = parsed.loop;
        }

        if (typeof parsed?.dots === 'boolean') {
          config.dots = parsed.dots;
        }

        if (typeof parsed?.controls === 'boolean') {
          config.controls = parsed.controls;
        }

        if (parsed?.transition === 'slide' || parsed?.transition === 'swipe' || parsed?.transition === 'fade') {
          config.transition = parsed.transition;
        }
      } catch (error) {
        config.transition = fallbackTransition;
      }

      return config;
    }

    getTransitionFromClass() {
      if (this.classList.contains('transition--swipe')) return 'swipe';
      if (this.classList.contains('transition--fade')) return 'fade';
      return 'slide';
    }

    prepareAnimations() {
      if (this.dataset.animationsReady === 'true') return;

      new globalThis.SplitText(this.querySelectorAll('.split-text'), {
        type: 'lines, words',
        linesClass: 'line-child'
      });

      globalThis.gsap.set(this.querySelectorAll('.subheading,.split-text,.button'), { visibility: 'visible' });
      this.dataset.animationsReady = 'true';
    }

    getAnimation(index) {
      if (this.animations[index]) return this.animations[index];

      const slide = this.slides[index];
      if (!slide) return null;

      const timeline = globalThis.gsap.timeline({ paused: true });
      let buttonOffset = 0;

      this.animations[index] = timeline;

      if (this.transitionType === 'swipe' && !this.useSwipePlugin) {
        timeline.fromTo(
          slide,
          { clipPath: 'polygon(0 0, 0 0, 0 100%, 0 100%)' },
          { duration: 0.7, clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)' },
          'start'
        );
      } else if (this.transitionType === 'fade' && !this.useFadePlugin) {
        timeline.fromTo(
          slide,
          { autoAlpha: 0 },
          { duration: 0.5, autoAlpha: 1 },
          'start'
        );
      }

      timeline.fromTo(
        slide.querySelector('.slideshow__slide-bg'),
        { scale: 1.08 },
        { duration: 1.5, scale: 1 },
        'start'
      );

      if (slide.querySelector('.subheading')) {
        timeline.to(
          slide.querySelector('.subheading'),
          { duration: 0.5, autoAlpha: 1 },
          0
        );
        buttonOffset += 0.5;
      }

      const headingLines = slide.querySelectorAll('.slideshow__slide-content--heading .line-child div');
      if (headingLines.length) {
        const headingDuration = 0.5 + (headingLines.length - 1) * 0.05;
        timeline.from(
          headingLines,
          { duration: headingDuration, yPercent: 100, stagger: 0.05 },
          0
        );
        buttonOffset += headingDuration;
      }

      const paragraphLines = slide.querySelectorAll('p:not(.subheading) .line-child div');
      if (paragraphLines.length) {
        const paragraphDuration = 0.5 + (paragraphLines.length - 1) * 0.02;
        timeline.from(
          paragraphLines,
          { duration: paragraphDuration, yPercent: 100, stagger: 0.02 },
          0
        );
        buttonOffset += paragraphDuration;
      }

      const buttons = slide.querySelectorAll('.button');
      if (buttons.length) {
        timeline.fromTo(
          buttons,
          { y: '100%' },
          { duration: 0.5, y: '0%', stagger: 0.1 },
          buttonOffset * 0.2
        );
      }

      return timeline;
    }

    animateSlide(index) {
      const animation = this.getAnimation(index);
      if (!animation) return;

      document.fonts.ready.then(() => {
        animation.timeScale(1).restart();
      });
    }

    animateReverse(index) {
      const animation = this.getAnimation(index);
      if (!animation) return;
      animation.timeScale(3).reverse();
    }

  }

  customElements.define('slideshow-embla', SlideshowEmbla);
}
